import { Injectable } from '@angular/core';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import type { XGritCompleteProduct } from '@core-mkt/services/xgrit-api/xgrit-product';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { EnvService } from '../env/env.service';

/**
 * Server-side implementation of PricingService for SSR.
 * Renders pricing HTML during server-side rendering so AI crawlers
 * and search engines can see pricing in the initial HTML.
 */
@Injectable({
  providedIn: 'root',
})
export class ServerPricingService {
  constructor(private redactor: WysiwygRedactorService, private env: EnvService) {}
  protected _couponCodeEvent: Subject<string> = new Subject<string>();

  crossedOutPrice: string;
  finalPrice: string;
  originalPrice: string;
  productFee: string;

  get couponCodeEvent(): Observable<string> {
    return this._couponCodeEvent.asObservable();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public sendCouponEvent(_coupon: string): void {
    // No-op on server side - coupon events are only processed client-side
    this._couponCodeEvent.next('');
  }

  /**
   * Generates pricing HTML for server-side rendering.
   * This mirrors the client-side PricingService logic to ensure
   * consistent pricing display for both crawlers and users.
   */
  getPricingText(product: XGritCompleteProduct, craftPricingText: string, priceAdjustment?: number): string {
    // Guard: Return empty if product data is missing
    if (!product?.apiData) {
      return '';
    }

    let pricingText: string;
    const feeTitle = this.env.get.brandConfig.feeLabel;
    this.setPricing(product);

    // Split prices to check if decimals are "00"
    const splitFinalPrice = this.finalPrice.split('.');
    const splitCrossedPrice = this.crossedOutPrice.split('.');

    // Apply price adjustment if provided (assumed to be a whole number)
    if (priceAdjustment && priceAdjustment !== 0) {
      splitFinalPrice[0] = (parseInt(splitFinalPrice[0]) + priceAdjustment).toString();
    }

    // Format prices (remove .00 decimals)
    this.finalPrice = splitFinalPrice[1] === '00' ? splitFinalPrice[0] : splitFinalPrice.join('.');
    this.crossedOutPrice = splitCrossedPrice[1] === '00' ? splitCrossedPrice[0] : splitCrossedPrice.join('.');
    const splitOriginalPrice = this.originalPrice.split('.');
    this.originalPrice = splitOriginalPrice[1] === '00' ? splitOriginalPrice[0] : splitOriginalPrice.join('.');

    // Handle custom Craft CMS pricing text with placeholders
    if (craftPricingText) {
      pricingText = this.replacePricingPlaceholders(craftPricingText);
      return this.redactor.bypassSanitizer(pricingText);
    }

    // Generate default pricing HTML based on product configuration
    pricingText = this.getDefaultPricingHtml(product);

    // Add product fee if applicable
    if (this.productFee !== '0.00' && this.env.get.brandConfig.showFee) {
      const feeClasses = 'product-fee-link !mt-0 !font-normal !text-sm !inline';
      pricingText += `<span class="product-fee"> + <span class="${feeClasses}">${feeTitle}</span></span>`;
    }

    return this.redactor.bypassSanitizer(pricingText);
  }

  /**
   * Sets pricing values from product API data.
   * Determines which prices to use based on CDI params and max price availability.
   */
  private setPricing(product: XGritCompleteProduct): void {
    const globalCDIQueryParam =
      this.env.get.brandConfig.cdiParamKey + '=' + this.env.get.brandConfig.cdiParamValueWhenEnabled;

    this.originalPrice = product.apiData.originalPrice != null ? product.apiData.originalPrice.toFixed(2) : '0';

    if (product.apiData.checkoutLink.includes(globalCDIQueryParam)) {
      // CDI (Custom Discount Identifier) pricing
      this.crossedOutPrice = product.apiData.originalPrice.toFixed(2);
      this.finalPrice = product.apiData.discountPrice.toFixed(2);
      this.productFee = product.apiData.productFees.toFixed(2);
    } else if (product.apiData.maxPrice && product.apiData.maxPrice !== product.apiData.originalPrice) {
      // Max price available and different from original
      this.crossedOutPrice = product.apiData.maxPrice.toFixed(2);
      this.finalPrice = product.apiData.discountPrice.toFixed(2);
      this.productFee = product.apiData.productFees.toFixed(2);
    } else if (product.apiData.disableRsa && product.apiData.originalPrice !== product.apiData.discountPrice) {
      // RSA-disabled products (DIP courses) with an active coupon discount.
      // originalPrice (grossTotal) = full/pre-coupon price; discountPrice (total) = coupon-reduced price.
      // These products exclude the CDI param from the checkout URL, so the CDI branch above is never taken.
      this.crossedOutPrice = product.apiData.originalPrice.toFixed(2);
      this.finalPrice = product.apiData.discountPrice.toFixed(2);
      this.productFee = product.apiData.productFees.toFixed(2);
    } else {
      // Standard pricing (no discount)
      this.crossedOutPrice = product.apiData.discountPrice.toFixed(2);
      this.finalPrice = product.apiData.discountPrice.toFixed(2);
      this.productFee = product.apiData.productFees.toFixed(2);
    }
  }

  /**
   * Replaces pricing placeholders in Craft CMS custom pricing text.
   * Handles ${this.crossedOutPrice} and ${this.finalPrice} placeholders.
   */
  private replacePricingPlaceholders(craftPricingText: string): string {
    const crossedOutString = '${this.crossedOutPrice}';
    const finalPriceString = '${this.finalPrice}';
    const originalPriceString = '${this.originalPrice}';
    let result = craftPricingText;

    // Replace crossed-out price placeholder
    let crossedOutIndex = result.indexOf(crossedOutString);
    if (crossedOutIndex !== -1) {
      result = result.slice(0, crossedOutIndex) + '<span class="orig-price">$' + result.slice(crossedOutIndex);

      crossedOutIndex = result.indexOf(crossedOutString);
      const crossedOutEndIndex = crossedOutIndex + crossedOutString.length;

      result = result.slice(0, crossedOutIndex) + this.crossedOutPrice + '</span>' + result.slice(crossedOutEndIndex);
    }

    // Replace final price placeholder
    let finalPriceIndex = result.indexOf(finalPriceString);
    if (finalPriceIndex !== -1) {
      result = result.slice(0, finalPriceIndex) + '<span class="disc-price">$' + result.slice(finalPriceIndex);

      finalPriceIndex = result.indexOf(finalPriceString);
      const finalPriceEndIndex = finalPriceIndex + finalPriceString.length;

      result = result.slice(0, finalPriceIndex) + this.finalPrice + '</span>' + result.slice(finalPriceEndIndex);
    }

    // Replace original price placeholder (no strikethrough styling)
    let originalPriceIndex = result.indexOf(originalPriceString);
    if (originalPriceIndex !== -1) {
      result = result.slice(0, originalPriceIndex) + '<span class="original-price">$' + result.slice(originalPriceIndex);

      originalPriceIndex = result.indexOf(originalPriceString);
      const originalPriceEndIndex = originalPriceIndex + originalPriceString.length;

      result =
        result.slice(0, originalPriceIndex) + this.originalPrice + '</span>' + result.slice(originalPriceEndIndex);
    }

    return result;
  }

  /**
   * Generates default pricing HTML based on product type and configuration.
   * Handles special cases for RE app, CDI pricing, and RSA configuration.
   */
  private getDefaultPricingHtml(product: XGritCompleteProduct): string {
    const globalCDIQueryParam =
      this.env.get.brandConfig.cdiParamKey + '=' + this.env.get.brandConfig.cdiParamValueWhenEnabled;
    const hasDiscount = this.crossedOutPrice !== this.finalPrice;

    // Handle CDI pricing display
    if (product.apiData.checkoutLink.includes(globalCDIQueryParam)) {
      if (product.apiData.app === 'RE') {
        return (
          'Starting at <span class="orig-price">' +
          (hasDiscount ? '$' + this.crossedOutPrice : '') +
          '</span> <span class="disc-price">$' +
          this.finalPrice +
          '</span>'
        );
      } else {
        if (!hasDiscount) {
          return '<span class="disc-price">$' + this.finalPrice + '</span>';
        } else {
          return (
            '<span class="orig-price-label">Regular</span> <span class="orig-price">$' +
            this.crossedOutPrice +
            ' </span>' +
            '<span>Sale Price</span> <span class="disc-price">$' +
            this.finalPrice +
            '</span>'
          );
        }
      }
    }

    // Handle RE app pricing display
    if (product.apiData.app === 'RE') {
      return (
        'Starting at <span class="orig-price">' +
        (hasDiscount ? '$' + this.crossedOutPrice : '') +
        '</span> <span class="disc-price">$' +
        this.finalPrice +
        '</span>'
      );
    }

    // Handle RSA disabled pricing
    if (product.apiData.disableRsa) {
      if (!hasDiscount) {
        return '<span class="disc-price">$' + this.finalPrice + '</span>';
      } else {
        return (
          '<span class="orig-price-label">Regular</span> <span class="orig-price">$' +
          this.crossedOutPrice +
          ' </span>' +
          '<span>Sale Price</span> <span class="disc-price">$' +
          this.finalPrice +
          '</span>'
        );
      }
    }

    // Default: Just show final price
    return '<span class="disc-price">$' + this.finalPrice + '</span>';
  }

  /**
   * Generates pricing HTML from parsed price data.
   * Simpler alternative to getPricingText when you have raw price numbers
   * instead of a full product object.
   */
  getPriceFromParsedData(price: number, maxPrice: number, fee?: number): string {
    const feeTitle = this.env.get.brandConfig.feeLabel;
    const parsedPrice = price.toFixed(2).split('.');
    const parsedMaxPrice = maxPrice.toFixed(2).split('.');

    let parsedFee: string[] | undefined;
    if (fee && fee > 0) {
      parsedFee = fee.toFixed(2).split('.');
    }

    const finalPrice = parsedPrice[1] === '00' ? parsedPrice[0] : parsedPrice.join('.');
    const crossedOutPrice = parsedMaxPrice[1] === '00' ? parsedMaxPrice[0] : parsedMaxPrice.join('.');

    let pricingText = '<span class="disc-price">$' + finalPrice + '</span>';

    if (maxPrice > price) {
      pricingText += '<span class="orig-price">$' + crossedOutPrice + '</span>';
    }

    if (fee && fee.toFixed(2) !== '0.00' && this.env.get.brandConfig.showFee && parsedFee) {
      const feeClasses = 'vision-button tertiary-button product-fee-link !mt-0 !font-normal !text-sm !inline';
      pricingText += `<span class="product-fee"> + <span class="${feeClasses}">$${
        parsedFee[1] === '00' ? parsedFee[0] : parsedFee.join('.')
      } ${feeTitle}</span></span>`;
    }

    return pricingText;
  }
}
